import { SCHEMAS } from '@workgrid/scim-filter'
import { concat, filter, find, reduce, some } from 'lodash'

import { useOrgUserAttributes } from '@/hooks/useOrgUserAttributes'

export const getDisplayName = (user: any) => {
  if (user.displayName) {
    return user.displayName
  } else {
    return `${user.givenName} ${user.familyName}`
  }
}

export const mapToUser = (user: any, currentUserEmail?: any, orgAdminRoleId?: any) => {
  const customAttributeKey = find(user.schemas, schema =>
    schema.startsWith('urn:ietf:params:scim:schemas:extension:workgrid:2.0:')
  )
  return {
    id: user.id,
    active: user.active,
    addresses: user.addresses,
    displayName: user.displayName,
    givenName: user.name && user.name.givenName,
    groups: user.groups,
    familyName: user.name && user.name.familyName,
    formatted: user.name && user.name.formatted,
    honorificPrefix: user.name && user.name.honorificPrefix,
    honorificSuffix: user.name && user.name.honorificSuffix,
    middleName: user.name && user.name.middleName,
    nickName: user.nickName,
    userName: user.userName,
    userType: user.userType,
    externalId: user.externalId,
    email: user.email,
    emails: user.emails,
    createdAt: user.createdAt,
    isCurrentUser: currentUserEmail ? user.email === currentUserEmail : false,
    timezone: user.timezone,
    locale: user.locale,
    phoneNumbers: user.phoneNumbers,
    photos: user.photos,
    preferredLanguage: user.preferredLanguage,
    profileUrl: user.profileUrl,
    title: user.title,
    roles: user.roles,
    employeeNumber: user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User']
      ? user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].employeeNumber
      : null,
    costCenter: user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User']
      ? user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].costCenter
      : null,
    department: user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User']
      ? user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].department
      : null,
    division: user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User']
      ? user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].division
      : null,
    organization: user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User']
      ? user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].organization
      : null,
    manager: user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User']
      ? user['urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'].manager
      : null,
    customAttributes: user[customAttributeKey] || null,
    schemas: user.schemas,
    isOrgAdmin: some(user.roles.organization[user.organizationId], role => role.id === orgAdminRoleId)
  }
}

// only selects attributes that comprise the individual rule set that aren't sub-attributes
export const individualNonComplexPredicate = ({
  scimAttributeName,
  scimParentAttributeName,
  scimSchema
}: {
  scimAttributeName: string
  scimParentAttributeName: string
  scimSchema: string
}) => {
  const isCoreSchemaMatch =
    scimSchema === SCHEMAS.CORE_USER &&
    !scimParentAttributeName &&
    ['displayName', 'externalId', 'id', 'nickName', 'userName'].includes(scimAttributeName)
  const isEnterpriseSchemaMatch = scimSchema === SCHEMAS.ENTERPRISE_USER && scimAttributeName === 'employeeNumber'

  return isCoreSchemaMatch || isEnterpriseSchemaMatch
}

// selects all attributes that comprise the individual rule set
export const targetingRuleSetsPredicate = (attribute: any) => {
  return individualNonComplexPredicate(attribute) || attribute.urn.startsWith(`${SCHEMAS.CORE_USER}:name.`)
}

export const complexAggregateAttributeToSubAttributeMap = {
  [`${SCHEMAS.CORE_USER}:addresses`]: 'formatted',
  [`${SCHEMAS.CORE_USER}:emails`]: 'value',
  [`${SCHEMAS.CORE_USER}:phoneNumbers`]: 'value',
  [`${SCHEMAS.CORE_USER}:groups`]: 'display',
  [`${SCHEMAS.ENTERPRISE_USER}:manager`]: 'displayName'
}

export function useTargetingAggregateAttributes() {
  return filter(useTargetingAttributes(), attribute => !targetingRuleSetsPredicate(attribute))
}

function useTargetingAttributes() {
  const orgUserAttributes = useOrgUserAttributes()
  return reduce(
    orgUserAttributes,
    (response, attribute) => {
      const { scimAttributeName, scimParentAttributeName, targetable, type, urn } = attribute
      const concatWithPredicate = (predicate: any, overrides?: any) => {
        return concat(
          response,
          filter(orgUserAttributes, predicate).map((attribute: any) => ({ ...attribute, ...overrides }))
        )
      }
      /*
       * Legacy configurations let a sub-attribute itself be targetable which is no longer supported. Certain required
       * attributes are also always targetable regardless of an organization's configuration.
       */
      if (!['externalId', 'id', 'userName'].includes(scimAttributeName) && (!targetable || scimParentAttributeName)) {
        return response
      }
      // only complex attributes are targetable but we only want specific sub-attributes to be used for aggregation
      if (complexAggregateAttributeToSubAttributeMap[urn]) {
        return concatWithPredicate(
          { urn: `${urn}.${complexAggregateAttributeToSubAttributeMap[urn]}` },
          { description: attribute.description }
        )
      }
      // for any remaining complex attributes, use all of it's sub-attributes for targeting (e.g. `name`)
      return type === 'complex'
        ? concatWithPredicate({ scimParentAttributeName: scimAttributeName })
        : concat(response, [{ ...attribute }])
    },
    [] as any[]
  )
}
